/**
 * Pure-data English catalogue for namespace 'ir' (W1-04 core-ir). Imports only core/util/types.
 *
 *  - lift.*   import report entries produced by `Lift.liftChart` (what lifting itself had to change or could not
 *             represent; normalisation entries come from formats/rpe, kept / flagged rows from `rpeKeptReport`);
 *             the entry's `path`, `line` and `count` are shown by the import report next to the text;
 *  - lower.*  export summaries of a `LowerReport` (`lowerReportMessages` in core/ir/lower.ts), shown by the export
 *             dialog.
 */

import type { MessageCatalog } from '../util/types';

const catalog: MessageCatalog = {
  ns: 'ir',
  locale: 'en',
  messages: {
    // ---- Lifting (import) ----
    'lift.eventSkipped':
      'An event with an invalid beat, a negative length, or a start before the previous event ended was left out.',
    'lift.noteSkipped': 'A note with an invalid beat was left out.',
    'lift.fatherMissing':
      'Line {line} names line {father} as its parent, but that line does not exist. The parent was removed.',
    'lift.fatherCycle':
      'Line {line} is its own ancestor through its parent lines (Phira refuses such a chart). Its parent was removed.',
    'lift.groupMissing':
      "Line {line} is in group {group}, which is not in the chart's group list. The line was put in no group (only RPE uses groups).",
    'lift.gifOnNonGif':
      'GIF progress on "{texture}", which is not a GIF, was kept only in the project file. Phira cannot load a chart that has it.',
    'lift.extraBpmRetimed':
      "extra.json has its own BPM list, different from the chart's. Effect and video times were converted through seconds.",
    'lift.unknownShader':
      'The effect shader "{shader}" is not a built-in shader and does not start with "/". It was read as the package file "{shader}".',
    'lift.videoAttachMissing':
      'A video is attached to line {line}, which does not exist (Phira would crash). The video was detached.',
    'lift.effectVarDuplicate': 'The effect variable "{name}" is set twice. The last value was used.',
    'lift.effectVarInvalid':
      'The value of the effect variable "{name}" could not be read. It was set to null (0 in Phira).',
    'lift.extraSliceOneKey':
      'An extra.json keyframe gives only one of easingLeft and easingRight. Phira uses 0 and 1 for a missing one; it is written with both keys, or with neither when they are 0 and 1.',
    'lift.videoVarNull':
      'A video sets "{which}" to null, which Phira reads as the default value. It is written without the "{which}" key.',
    'lift.videoTimeNormalized':
      'The video start beat is not in lowest terms. It is written in lowest terms (Phira plays it at the same time).',

    // ---- Lowering (export) ----
    'lower.bakedSpeed':
      '{segments, plural, one {# eased scroll-speed segment was} other {# eased scroll-speed segments were}} split into {pieces, plural, one {# linear piece} other {# linear pieces}} for Classic speed mode. The largest floor-position difference is {error} px.',
    'lower.bakedSpeedInexact':
      '{count, plural, one {# scroll-speed segment uses} other {# scroll-speed segments use}} a curve slice Phira cannot evaluate and {count, plural, one {was} other {were}} exported as one linear piece.',
    'lower.paddedLines':
      '{count, plural, one {# line was} other {# lines were}} padded with neutral events and RPE default values so RPE opens {count, plural, one {it} other {them}} the same way. Phira shows no difference.',
    'lower.staleRawTriples':
      '{count, plural, one {# imported beat value was} other {# imported beat values were}} out of date and written in normalised form.',
    'lower.gapsMaterialised':
      '{count, plural, one {# hold could} other {# holds could}} not be written as a gap between events and {count, plural, one {was} other {were}} written as a constant event.',
  },
};

export default catalog;
